import React, { useState, useEffect } from 'react';
import { Search, X, Loader, Menu } from 'lucide-react';
import axios from 'axios';
import {Link, useLocation, useSearchParams} from 'react-router-dom';
import { useLanguage } from '../utils/LanguageContext';
import {Helmet} from "react-helmet-async";
import getHelmetConfig from "../utils/helmetConfig";

const API_BASE_URL = 'https://texla.uz/api/v1';

// Tilga qarab matn qaytarish
const getLocalizedText = (obj, language) => {
    if (!obj) return '';
    const key = `title_${language}`;
    return obj[key] || obj.title_uz || obj.title || '';
};

// Tilga qarab product nomi qaytarish
const getLocalizedProductName = (product, language) => {
    if (!product) return '';
    const key = `name_${language}`;
    return product[key] || product.name_uz || product.name || '';
};

const ShopPage = ({ onNavigate, initialFilters = {} }) => {
    const { t, language } = useLanguage();
    const [searchParams] = useSearchParams();

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [formData, setFormData] = useState({ name: '', phone: '' });
    const [submitted, setSubmitted] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    // URL parametrlaridan olish
    const urlCategory = searchParams.get('category');
    const urlBrand = searchParams.get('brand');

    const [filters, setFilters] = useState({
        category: urlCategory || initialFilters.category || '',
        brand: urlBrand || initialFilters.brand || '',
        search: initialFilters.search || '',
        sortBy: 'newest',
        page: 1
    });

    const [searchInput, setSearchInput] = useState(initialFilters.search || '');
    const [totalProducts, setTotalProducts] = useState(0);

    useEffect(() => {
        loadFiltersData();
    }, [language]);

    useEffect(() => {
        loadProducts();
    }, [filters, language]);

    const location = useLocation();

    useEffect(() => {
        const newCategory = searchParams.get('category');
        const newBrand = searchParams.get('brand');
        const newSearch = searchParams.get('search');

        setFilters(prev => ({
            ...prev,
            category: newCategory || '',
            brand: newBrand || '',
            search: newSearch || '',
            page: 1
        }));
        window.scrollTo(0, 0);
    }, [location.search]);

    const loadFiltersData = async () => {
        try {
            const [categoriesRes, brandsRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/categories/`),
                axios.get(`${API_BASE_URL}/brands/`)
            ]);
            setCategories(categoriesRes.data || []);
            setBrands(brandsRes.data || []);
        } catch (error) {
        }
    };

    const loadProducts = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.category) params.categories = filters.category;
            if (filters.brand) params.brands = filters.brand;
            if (filters.search) params.search = filters.search;

            // ← Backend pagination parameterlari
            params.page = filters.page;
            params.page_size = 12;  // Har sahifada 12 ta mahsulot

            // Sort parameter
            if (filters.sortBy === 'asc') {
                params.ordering = 'name_uz';
            } else if (filters.sortBy === 'desc') {
                params.ordering = '-name_uz';
            } else {
                params.ordering = '-created_at';  // Default: newest
            }

            const response = await axios.get(`${API_BASE_URL}/products/`, { params });

            // Backend'dan pagination response keladi: {count, next, previous, results}
            const productsData = response.data.results || response.data;
            const totalCount = response.data.count || productsData.length;

            setProducts(productsData);
            setTotalProducts(totalCount);
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: String(value), page: 1 }));
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setFilters(prev => ({ ...prev, search: searchInput, page: 1 }));
    };

    const handleResetFilters = () => {
        setFilters({
            category: '',
            brand: '',
            search: '',
            sortBy: 'newest',
            page: 1
        });
        setSearchInput('');
    };

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [filters])

    const handlePageChange = (newPage) => {
        setFilters(prev => ({ ...prev, page: newPage }));
    };

    const handleRequestClick = (e, product) => {
        e.stopPropagation();
        setSelectedProduct(product);
        setShowModal(true);
        setSubmitted(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.phone || !selectedProduct) return;

        try {
            await axios.post(`${API_BASE_URL}/zayavka/`, {
                product: selectedProduct.id,
                name: formData.name,
                phone: formData.phone
            });

            setSubmitted(true);
            setTimeout(() => {
                setShowModal(false);
                setSubmitted(false);
                setFormData({ name: '', phone: '' });
                setSelectedProduct(null);
            }, 3000);
        } catch (error) {
        }
    };

    const totalPages = Math.ceil(totalProducts / 12);

    // Tanlangan kategoria nomi
    const selectedCategoryName = categories.find(cat => cat.id === parseInt(filters.category))?.[`title_${language}`]
        || categories.find(cat => cat.id === parseInt(filters.category))?.title_uz
        || '';

    // Tanlangan brend nomi
    const selectedBrandName = brands.find(brand => brand.id === parseInt(filters.brand))?.[`title_${language}`]
        || brands.find(brand => brand.id === parseInt(filters.brand))?.title_uz
        || '';

    const helmetConfig = getHelmetConfig(language);
    const pageConfig = helmetConfig.shop;

    return (
        <div className="min-h-screen bg-white">
            <Helmet>
                <title>{pageConfig.title}</title>
                <meta name="description" content={pageConfig.description} />
                <meta name="keywords" content={pageConfig.keywords} />
            </Helmet>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-semibold text-gray-900 mb-1">
                        {selectedCategoryName || selectedBrandName || t('shop.products')}
                    </h1>
                    <p className="text-sm text-gray-500">
                        {totalProducts} {t('shop.productsFound')}
                    </p>
                </div>

                <div className="flex gap-6">
                    {/* Sidebar */}
                    <div className={`fixed inset-y-0 left-0 z-40 w-64 bg-white transform transition-transform duration-300 lg:relative lg:transform-none lg:z-0 overflow-y-auto ${
                        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
                    }`}>
                        <div className="p-2 pt-6 mt-6 md:p-0 md:m-0">
                            {/* Close button for mobile */}
                            <button
                                onClick={() => setSidebarOpen(false)}
                                className="lg:hidden absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                            >
                                <X size={24} />
                            </button>

                            {/* Search Bar */}
                            <div className="mb-6">
                                <form onSubmit={handleSearchSubmit} className="flex gap-2">
                                    <input
                                        type="text"
                                        placeholder={t('shop.search')}
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-300 focus:border-transparent outline-none text-sm"
                                    />
                                    <button
                                        type="submit"
                                        className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition"
                                    >
                                        <Search size={18} />
                                    </button>
                                </form>
                            </div>

                            {/* Categories Filter */}
                            <div className="mb-6">
                                <h3 className="font-semibold text-gray-900 mb-3 text-sm">{t('shop.categories')}</h3>
                                <div className="space-y-2">
                                    <button
                                        onClick={() => handleFilterChange('category', '')}
                                        className={`block w-full text-left px-3 py-2 rounded transition text-sm ${
                                            !filters.category
                                                ? 'bg-orange-600 text-white font-medium'
                                                : 'hover:bg-gray-100'
                                        }`}
                                    >
                                        {t('shop.all')}
                                    </button>
                                    {/* Max 10 ta ko'rinadi, qolganini scroll bilan */}
                                    <div className="max-h-64 overflow-y-auto">
                                        {categories.map(cat => (
                                            <button
                                                key={cat.id}
                                                onClick={() => handleFilterChange('category', cat.id)}
                                                className={`block w-full text-left px-3 py-2 rounded transition text-sm ${
                                                    filters.category === String(cat.id)
                                                        ? 'bg-orange-600 text-white font-medium'
                                                        : 'hover:bg-gray-100'
                                                }`}
                                            >
                                                {getLocalizedText(cat, language)}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Brands Filter */}
                            <div className="mb-6">
                                <h3 className="font-semibold text-gray-900 mb-3 text-sm">{t('shop.brands')}</h3>
                                <div className="space-y-2">
                                    <button
                                        onClick={() => handleFilterChange('brand', '')}
                                        className={`block w-full text-left px-3 py-2 rounded transition text-sm ${
                                            !filters.brand
                                                ? 'bg-orange-600 text-white font-medium'
                                                : 'hover:bg-gray-100'
                                        }`}
                                    >
                                        {t('shop.all')}
                                    </button>
                                    {/* Max 10 ta ko'rinadi, qolganini scroll bilan */}
                                    <div className="max-h-64 overflow-y-auto">
                                        {brands.map(brand => (
                                            <button
                                                key={brand.id}
                                                onClick={() => handleFilterChange('brand', brand.id)}
                                                className={`block w-full text-left px-3 py-2 rounded transition text-sm ${
                                                    filters.brand === String(brand.id)
                                                        ? 'bg-orange-600 text-white font-medium'
                                                        : 'hover:bg-gray-100'
                                                }`}
                                            >
                                                {getLocalizedText(brand, language)}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Reset Filters */}
                            {(filters.category || filters.brand || filters.search) && (
                                <button
                                    onClick={handleResetFilters}
                                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 px-4 py-2 rounded-lg transition font-medium text-sm"
                                >
                                    {t('shop.resetFilters')}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="flex-1">
                        {/* Mobile Menu Toggle */}
                        <button
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                            className="lg:hidden mb-4 flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition"
                        >
                            <Menu size={18} />
                            <span className="text-sm">{t('shop.filters')}</span>
                        </button>

                        {/* Products Grid */}
                        {loading ? (
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {[...Array(12)].map((_, i) => (
                                    <div key={i} className="bg-gray-200 rounded-lg h-64 animate-pulse" />
                                ))}
                            </div>
                        ) : products.length > 0 ? (
                            <>
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
                                    {products.map(product => (
                                        <Link key={product.id} to={`/product/${product.id}`} className="group">
                                            <div className="bg-white rounded-lg shadow hover:shadow-lg transition overflow-hidden h-full flex flex-col">
                                                <div className="relative h-48 bg-gray-100 overflow-hidden flex-shrink-0">
                                                    <img
                                                        src={product.main_image || 'https://via.placeholder.com/300x300'}
                                                        alt={getLocalizedProductName(product, language)}
                                                        className="w-full h-full object-cover group-hover:scale-110 transition duration-300"
                                                    />
                                                </div>
                                                <div className="p-2 md:p-4 flex-1 flex flex-col ">
                                                    <span className="text-xs text-gray-500 mb-1 block">
                                                        {product.code}
                                                    </span>
                                                    <h3 className="font-medium text-gray-900 mb-2 line-clamp-2 text-sm flex-1">
                                                        {getLocalizedProductName(product, language)}
                                                    </h3>
                                                    {product.brands && product.brands.length > 0 && (
                                                        <p className="text-xs text-gray-500 mb-3">
                                                            {getLocalizedText(product.brands[0], language)}
                                                        </p>
                                                    )}
                                                    <button
                                                        onClick={(e) => handleRequestClick(e, product)}
                                                        className="w-full bg-orange-600 hover:bg-orange-700 text-white py-2 rounded transition font-medium text-sm"
                                                    >
                                                        {t('shop.sendRequest')}
                                                    </button>
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="flex items-center justify-center space-x-2">
                                        <button
                                            onClick={() => handlePageChange(filters.page - 1)}
                                            disabled={filters.page === 1}
                                            className="px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm font-medium"
                                        >
                                            ← {t('shop.back')}
                                        </button>

                                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (filters.page <= 3) {
                                                pageNum = i + 1;
                                            } else if (filters.page >= totalPages - 2) {
                                                pageNum = totalPages - 4 + i;
                                            } else {
                                                pageNum = filters.page - 2 + i;
                                            }

                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => handlePageChange(pageNum)}
                                                    className={`px-3 py-2 rounded-lg transition text-sm font-medium ${
                                                        filters.page === pageNum
                                                            ? 'bg-orange-600 text-white'
                                                            : 'bg-white border border-gray-200 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}

                                        <button
                                            onClick={() => handlePageChange(filters.page + 1)}
                                            disabled={filters.page === totalPages}
                                            className="px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm font-medium"
                                        >
                                            {t('shop.next')} →
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-20 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="text-6xl mb-4">🔍</div>
                                <h3 className="text-xl font-medium text-gray-900 mb-2">{t('shop.notFound')}</h3>
                                <p className="text-sm text-gray-600 mb-6">{t('shop.tryOtherFilters')}</p>
                                <button
                                    onClick={handleResetFilters}
                                    className="bg-orange-600 text-white px-6 py-2 rounded-lg hover:bg-orange-700 transition text-sm font-medium"
                                >
                                    {t('shop.resetFilters')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Inquiry Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
                        <button
                            onClick={() => setShowModal(false)}
                            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
                        >
                            <X size={24} />
                        </button>

                        {!submitted ? (
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    {t('modal.title')}
                                </h3>
                                <p className="text-xs text-gray-600 font-medium mb-1">{selectedProduct?.code}</p>
                                <p className="text-gray-800 mb-6 font-medium text-sm">
                                    {getLocalizedProductName(selectedProduct, language)}
                                </p>

                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('modal.fullName')} *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-300 focus:border-transparent outline-none text-sm"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('modal.phone')} *
                                        </label>
                                        <input
                                            type="tel"
                                            required
                                            value={formData.phone}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-300 focus:border-transparent outline-none text-sm"
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-3 rounded-lg transition"
                                    >
                                        {t('modal.send')}
                                    </button>
                                </form>
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                    {t('modal.successTitle')}
                                </h4>
                                <p className="text-sm text-gray-600">
                                    {t('modal.successMessage')}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShopPage;